/**
 * ConversationListItem
 * Unified conversation list item for the "All" tab, showing type badges and archive action.
 */

import { Archive, Users } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { toast } from "sonner"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import ritualityAiIcon from "@/assets/icons/rituality-ai.svg"
import ritualsLinkIcon from "@/assets/icons/rituals-link.svg"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useUpdateAiConversation } from "../../hooks/useAiChat"
import { useArchiveConversation } from "../../hooks/useUnifiedConversations"
import type { UnifiedConversation } from "../../types/unified-chat.types"
import { getConversationRoute } from "../../utils/conversationRoutes"
import { formatTimeAgo } from "../../utils/formatTimeAgo"
import { UnreadBadge } from "./UnreadBadge"

function ConversationIcon({ conversation }: { conversation: UnifiedConversation }) {
  if (conversation.type === "ai") {
    return (
      <img src={aiAvatarImg} alt="Rituality AI" className="size-[30px] rounded-full shrink-0" />
    )
  }

  if (conversation.type === "ritual-group") {
    if (conversation.profilePhoto) {
      return (
        <img
          src={conversation.profilePhoto}
          alt=""
          className="size-[30px] rounded-full object-cover shrink-0"
        />
      )
    }
    return (
      <div className="size-[30px] rounded-full bg-primary/20 flex items-center justify-center shrink-0">
        <img src={ritualsLinkIcon} alt="" className="size-4 opacity-70" />
      </div>
    )
  }

  return (
    <Avatar
      src={conversation.profilePhoto || undefined}
      alt={conversation.name}
      fallback={conversation.name || "?"}
      size="md"
    />
  )
}

function TypeBadge({ conversation }: { conversation: UnifiedConversation }) {
  if (conversation.type === "ai") {
    return (
      <div className="flex items-center gap-1.5">
        <img src={ritualityAiIcon} alt="" className="size-3.5" />
        <span className="text-[13px] font-semibold text-brand-olive">Rituality AI</span>
      </div>
    )
  }
  if (conversation.type === "ritual-group") {
    return (
      <div className="flex items-center gap-1.5">
        <Users className="size-3.5 text-brand-terracotta" />
        <span className="text-[13px] font-semibold text-brand-terracotta">Organizers</span>
      </div>
    )
  }
  if (conversation.type === "dm" && conversation.metadata?.ritualId) {
    return (
      <div className="flex items-center gap-1.5">
        <Users className="size-3.5 text-brand-terracotta" />
        <span className="text-[13px] font-semibold text-brand-terracotta">Coaching Session</span>
      </div>
    )
  }
  return null
}

export function ConversationListItem({
  conversation,
  showTypeBadge = false,
}: {
  conversation: UnifiedConversation
  showTypeBadge?: boolean
}) {
  const location = useLocation()
  const route = getConversationRoute(conversation)
  const isActive = location.pathname === route
  const archiveDmConversation = useArchiveConversation()
  const updateAiConversation = useUpdateAiConversation()

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (conversation.type === "ai") {
      updateAiConversation.mutate(
        { id: conversation.id, data: { status: "archived" } },
        {
          onSuccess: () => toast.success("Conversation archived"),
          onError: () => toast.error("Failed to archive conversation"),
        },
      )
    } else {
      archiveDmConversation.mutate(conversation.id, {
        onSuccess: () => toast.success("Conversation archived"),
        onError: () => toast.error("Failed to archive conversation"),
      })
    }
  }

  return (
    <Link
      to={route}
      className={cn(
        "group relative w-full flex flex-col gap-[10px] px-[20px] py-[15px] text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      {/* Top row: avatar + name + time */}
      <div className="flex items-center h-[30px]">
        <div className="flex items-center gap-[10px] flex-1 min-w-0">
          <ConversationIcon conversation={conversation} />
          <p className="text-[13px] font-semibold text-foreground truncate">
            {conversation.name || "New conversation"}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[13px] text-muted-foreground/70 group-hover:hidden">
            {formatTimeAgo(conversation.lastMessageAt ?? conversation.createdAt)}
          </span>
          {(conversation.type === "dm" || conversation.type === "ai") && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleArchive}
              className="hidden group-hover:flex hover:bg-muted text-muted-foreground"
            >
              <Archive className="size-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Message preview */}
      <p className="text-[13px] font-normal text-foreground leading-[25px] line-clamp-2">
        {conversation.lastMessagePreview || "No messages yet"}
      </p>

      {/* Type badge */}
      {showTypeBadge && <TypeBadge conversation={conversation} />}

      {/* Unread badge - absolute bottom right */}
      <UnreadBadge
        count={conversation.unreadCount}
        className="absolute bottom-[14px] right-[20px]"
      />
    </Link>
  )
}
